import { Canvas3d } from "../display3d/Canvas3d.js";

export interface MarkerOptions {
    lat?: number;
    lng?: number;
    originX?: string;
    originY?: string;
    zIndexOffset?: number;
    offsetX?: number;
    offsetY?: number;
    round?: boolean;
    minZoom?: number;
    maxZoom?: number;
    interactive?: boolean;
}

export interface MarkerUpdateInfo {
    x: number;
    y: number;
    cameraDistance: number;
    zoom: number;
}

export class Marker {
    public readonly element: HTMLDivElement;

    public readonly position: THREE.Vector3;

    public x: number = 0;
    public y: number = 0;
    public cameraDistance: number = 0;

    public zIndexOffset: number = 0;

    private _originX: string;
    private _originY: string;
    private _offsetX: number = 0;
    private _offsetY: number = 0;
    private _round: boolean;
    private _minZoom: number;
    private _maxZoom: number;
    private _opacity: number = 0;
    private _targetOpacity: number = 0;

    constructor(private _canvas: Canvas3d, options: MarkerOptions) {
        options = e5.object.merge({
            lat: 0,
            lng: 0,
            originX: "50%",
            originY: "100%",
            offsetX: 0,
            offsetY: 0,
            zIndexOffset: 0,
            round: false,
            minZoom: -1000,
            maxZoom: 1000
        } as MarkerOptions, options);

        this._minZoom = options.minZoom;
        this._maxZoom = options.maxZoom;
        this._round = options.round;
        this.zIndexOffset = options.zIndexOffset;
        this._offsetX = options.offsetX;
        this._offsetY = options.offsetY;
        this._originX = this.prepareOrigin(options.originX);
        this._originY = this.prepareOrigin(options.originY);

        this.element = document.createElement("div");
        this.element.style.opacity = "0";
        this.element.className = "awi-marker";
        this.element.style.transformOrigin = `${options.originX} ${options.originY}`;
        this.position = _canvas.lngLatToVector(options.lng, options.lat);

        if (options.interactive) {
            this.element.classList.add("awi-marker--interactive");
        }
    }

    private prepareOrigin(origin:string):string{
        if(origin.indexOf("calc") !== -1){
            return origin.replace("-", "+").replace("calc(", "calc(-");
        } else {
            return "-" + origin;
        }
    }

    private get opacity(): number {
        return this._opacity;
    }

    private set opacity(value: number) {
        value = e5.Calc.clamp(value, 0, 1);
        if (this._opacity === value) {
            return;
        }
        this._opacity = value;
        this.element.style.opacity = value + "";
        this.element.classList.toggle("e5-hidden", value <= 0);
    }

    private _lastX: number = 0;
    private _lastY: number = 0;

    public update(info: MarkerUpdateInfo): void {

        /**
         * Calc ratio
         */
        var dist = this.position.distanceTo(this._canvas.camera.position);
        var delta = dist - info.cameraDistance;
        var start = -50;
        var end = 0;
        var ratio = 1;
        if (delta > start) {
            ratio = e5.Calc.clamp(e5.Calc.ratio(delta, end, start), 0, 1);
        }

        /**
         * Apply opacity
         */
        var opacity = ratio;
        if (info.zoom < this._minZoom
            || info.zoom > this._maxZoom) {
            opacity = 0;
        }
        this._targetOpacity = opacity;
        var a = this.opacity + (this._targetOpacity - this.opacity) * 0.2;
        var mar = 0.01;
        if (a < mar) {
            a = 0;
        } else if (a > (1 - mar)) {
            a = 1;
        }
        this.opacity = a;

        /**
         * Apply transform
         */
        if (this.opacity > 0) {
            var x = info.x + this._offsetX;
            var y = info.y + this._offsetY;

            if (this._lastX == x
                && this._lastY == y) {
                if (this._round) {
                    x = Math.round(x);
                    y = Math.round(y);
                }
            } else {
                this._lastX = x;
                this._lastY = y;
            }

            var scale = 0.5 + (ratio * 0.5);

            this.element.style.transform = `translate3d(${x}px, ${y}px, 0) translate(${this._originX}, ${this._originY}) scale(${scale})`;
        }
    }
}